import React, { useEffect, useRef, useState } from 'react';
import { AuthMethod, Profile } from '../types';
import { EmailSignupForm } from './EmailSignupForm';
import { EmailLoginForm } from './EmailLoginForm';
import { PasswordResetForm } from './PasswordResetForm';
import './AuthMenu.css';

type FormView = 'menu' | 'signup' | 'login' | 'reset';

interface AuthMenuProps {
  isOpen: boolean;
  isAuthenticated: boolean;
  user: Profile | null;
  onClose: () => void;
  onAuthMethodSelect: (method: AuthMethod) => void;
  onEmailSignup: (email: string, password: string, name?: string) => Promise<void>;
  onEmailLogin: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  onPasswordReset: (email: string) => Promise<void>;
  onLogout: () => void;
  onViewStatistics: () => void;
  onViewSettings: () => void;
  onViewPreferences: () => void;
  onViewAbout: () => void;
  isMobile?: boolean;
  isLoading?: boolean;
}

/**
 * AuthMenu Component
 * Displays authentication options for guests or user menu for authenticated users
 * Hybrid design: dropdown on desktop/tablet, bottom sheet on mobile
 * Includes email signup, login, and password reset forms
 */
export const AuthMenu: React.FC<AuthMenuProps> = ({
  isOpen,
  isAuthenticated,
  user,
  onClose,
  onAuthMethodSelect,
  onEmailSignup,
  onEmailLogin,
  onPasswordReset,
  onLogout,
  onViewStatistics,
  onViewSettings,
  onViewPreferences,
  onViewAbout,
  isMobile = false,
  isLoading = false
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [currentView, setCurrentView] = useState<FormView>('menu');

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        backdropRef.current &&
        backdropRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Handle Escape key to close
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when menu is open on mobile
  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, isMobile]);

  // Reset view when menu closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentView('menu');
    }
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !menuRef.current) return;

    const focusableElements = menuRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    firstElement.focus();

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  if (!isOpen) return null;

  // Form navigation handlers
  const handleEmailSignupClick = () => {
    setCurrentView('signup');
  };

  const handleEmailLoginClick = () => {
    setCurrentView('login');
  };

  const handleBackToMenu = () => {
    setCurrentView('menu');
  };

  const handleForgotPassword = () => {
    setCurrentView('reset');
  };

  const handleSwitchToLogin = () => {
    setCurrentView('login');
  };

  const handleSwitchToSignup = () => {
    setCurrentView('signup');
  };

  // Render form based on current view
  const renderContent = () => {
    if (isAuthenticated) {
      return renderAuthenticatedMenu();
    }

    switch (currentView) {
      case 'signup':
        return (
          <EmailSignupForm
            onSubmit={onEmailSignup}
            onBackToMenu={handleBackToMenu}
            onSwitchToLogin={handleSwitchToLogin}
            isLoading={isLoading}
          />
        );
      
      case 'login':
        return (
          <EmailLoginForm
            onSubmit={onEmailLogin}
            onBackToMenu={handleBackToMenu}
            onForgotPassword={handleForgotPassword}
            onSwitchToSignup={handleSwitchToSignup}
            isLoading={isLoading}
          />
        );
      
      case 'reset':
        return (
          <PasswordResetForm
            onSubmit={onPasswordReset}
            onBackToLogin={handleSwitchToLogin}
            isLoading={isLoading}
          />
        );
      
      case 'menu':
      default:
        return renderGuestMenu();
    }
  };

  const renderGuestMenu = () => (
    <>
      <h2 id="auth-menu-title" className="auth-menu-title">
        🎮 Welcome to PlayHub Games
      </h2>

      <div className="auth-menu-content">
        {/* OAuth Providers */}
        <button
          className={getAuthMethodClass(AuthMethod.GOOGLE)}
          onClick={() => onAuthMethodSelect(AuthMethod.GOOGLE)}
          type="button"
        >
          <span className="auth-menu-button-icon">{getAuthMethodIcon(AuthMethod.GOOGLE)}</span>
          <span className="auth-menu-button-text">{getAuthMethodLabel(AuthMethod.GOOGLE)}</span>
          <span className="auth-menu-button-arrow">→</span>
        </button>

        <button
          className={getAuthMethodClass(AuthMethod.FACEBOOK)}
          onClick={() => onAuthMethodSelect(AuthMethod.FACEBOOK)}
          type="button"
        >
          <span className="auth-menu-button-icon">{getAuthMethodIcon(AuthMethod.FACEBOOK)}</span>
          <span className="auth-menu-button-text">{getAuthMethodLabel(AuthMethod.FACEBOOK)}</span>
          <span className="auth-menu-button-arrow">→</span>
        </button>

        <button
          className={getAuthMethodClass(AuthMethod.YAHOO)}
          onClick={() => onAuthMethodSelect(AuthMethod.YAHOO)}
          type="button"
        >
          <span className="auth-menu-button-icon">{getAuthMethodIcon(AuthMethod.YAHOO)}</span>
          <span className="auth-menu-button-text">{getAuthMethodLabel(AuthMethod.YAHOO)}</span>
          <span className="auth-menu-button-arrow">→</span>
        </button>

        {/* Divider */}
        <div className="auth-menu-divider">
          <span>OR</span>
        </div>

        {/* Email Auth */}
        <button
          className={getAuthMethodClass(AuthMethod.CUSTOM)}
          onClick={handleEmailSignupClick}
          type="button"
        >
          <span className="auth-menu-button-icon">{getAuthMethodIcon(AuthMethod.CUSTOM)}</span>
          <span className="auth-menu-button-text">{getAuthMethodLabel(AuthMethod.CUSTOM)}</span>
          <span className="auth-menu-button-arrow">→</span>
        </button>

        <button
          className="auth-menu-button auth-menu-button-login"
          onClick={handleEmailLoginClick}
          type="button"
        >
          <span className="auth-menu-button-icon">🔑</span>
          <span className="auth-menu-button-text">Login with Email</span>
          <span className="auth-menu-button-arrow">→</span>
        </button>

        {/* Divider */}
        <div className="auth-menu-divider-line" />

        {/* Guest Mode */}
        <button
          className={getAuthMethodClass(AuthMethod.GUEST)}
          onClick={() => onAuthMethodSelect(AuthMethod.GUEST)}
          type="button"
        >
          <span className="auth-menu-button-icon">{getAuthMethodIcon(AuthMethod.GUEST)}</span>
          <span className="auth-menu-button-text">{getAuthMethodLabel(AuthMethod.GUEST)}</span>
          <span className="auth-menu-button-arrow">→</span>
        </button>
      </div>

      {/* Mobile Cancel Button */}
      {isMobile && (
        <button
          className="auth-menu-cancel"
          onClick={onClose}
          type="button"
        >
          Cancel
        </button>
      )}
    </>
  );

  const renderAuthenticatedMenu = () => (
    <>
      <div className="auth-menu-profile">
        <div className="auth-menu-profile-info">
          <h2 id="auth-menu-title" className="auth-menu-profile-name">
            {user?.name}
          </h2>
          <p className="auth-menu-profile-email">{user?.email}</p>
          <p className="auth-menu-profile-auth">
            via {formatAuthMethod(user?.authMethod || AuthMethod.GUEST)} {getAuthMethodIcon(user?.authMethod || AuthMethod.GUEST)}
          </p>
          {user?.createdAt && (
            <p className="auth-menu-profile-member">
              Member since {formatDate(user.createdAt)}
            </p>
          )}
        </div>
      </div>

      <div className="auth-menu-divider-line" />

      <div className="auth-menu-content">
        <button
          className="auth-menu-item"
          onClick={onViewStatistics}
          type="button"
        >
          <span className="auth-menu-item-icon">📊</span>
          <span className="auth-menu-item-text">My Statistics</span>
        </button>

        <button
          className="auth-menu-item"
          onClick={onViewSettings}
          type="button"
        >
          <span className="auth-menu-item-icon">⚙️</span>
          <span className="auth-menu-item-text">Settings</span>
        </button>

        <button
          className="auth-menu-item"
          onClick={onViewPreferences}
          type="button"
        >
          <span className="auth-menu-item-icon">🎨</span>
          <span className="auth-menu-item-text">Preferences</span>
        </button>

        <button
          className="auth-menu-item"
          onClick={onViewAbout}
          type="button"
        >
          <span className="auth-menu-item-icon">ℹ️</span>
          <span className="auth-menu-item-text">About</span>
        </button>
      </div>

      <div className="auth-menu-divider-line" />

      <button
        className="auth-menu-logout"
        onClick={onLogout}
        type="button"
      >
        <span className="auth-menu-item-icon">🔓</span>
        <span className="auth-menu-item-text">Logout</span>
      </button>
    </>
  );

  if (!isOpen) return null;

  const getAuthMethodIcon = (method: AuthMethod): string => {
    switch (method) {
      case AuthMethod.GOOGLE:
        return 'G';
      case AuthMethod.FACEBOOK:
        return 'f';
      case AuthMethod.APPLE:
        return '';
      case AuthMethod.YAHOO:
        return 'Y!';
      case AuthMethod.CUSTOM:
        return '✉';
      case AuthMethod.GUEST:
        return '👤';
      default:
        return '🔑';
    }
  };

  const getAuthMethodLabel = (method: AuthMethod): string => {
    switch (method) {
      case AuthMethod.GOOGLE:
        return 'Continue with Google';
      case AuthMethod.FACEBOOK:
        return 'Continue with Facebook';
      case AuthMethod.APPLE:
        return 'Continue with Apple';
      case AuthMethod.YAHOO:
        return 'Continue with Yahoo';
      case AuthMethod.CUSTOM:
        return 'Sign up with Email';
      case AuthMethod.GUEST:
        return 'Continue as Guest';
      default:
        return 'Login';
    }
  };

  const getAuthMethodClass = (method: AuthMethod): string => {
    return `auth-menu-button auth-menu-button-${method}`;
  };

  const formatAuthMethod = (method: AuthMethod): string => {
    return method.charAt(0).toUpperCase() + method.slice(1);
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: 'numeric'
    }).format(new Date(date));
  };

  return (
    <>
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className={`auth-menu-backdrop ${isMobile ? 'auth-menu-backdrop-mobile' : ''}`}
        aria-hidden="true"
      />

      {/* Menu */}
      <div
        ref={menuRef}
        className={`auth-menu ${isMobile ? 'auth-menu-mobile' : 'auth-menu-desktop'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-menu-title"
      >
        {/* Mobile drag handle */}
        {isMobile && currentView === 'menu' && (
          <div className="auth-menu-drag-handle" aria-hidden="true">
            <div className="auth-menu-drag-bar" />
          </div>
        )}

        {renderContent()}
      </div>
    </>
  );
};

export default AuthMenu;
